import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { CrewTab } from '../crew/CrewTab';
import { CREW_RESOURCE } from '../crew/types';
import { HistoryPanel } from '../history/HistoryPanel';
import { PacklistTab } from '../packlists/PacklistTab';
import { InputlistTab } from '../inputlists/InputlistTab';
import { INPUTLISTS_RESOURCE } from '../inputlists/types';
import { PACKLISTS_RESOURCE } from '../packlists/types';
import { ChecklistPanel } from '../planning/ChecklistPanel';
import { RemindersSection } from '../packlists/RemindersSection';
import { QuoteTab } from '../quotes/QuoteTab';
import type { GoToQuoteNotice } from '../quotes/GenerateMenu';
import { ProfitTab } from '../finance/ProfitTab';
import { EVENT_COSTS_RESOURCE, PROFIT_RESOURCE } from '../finance/types';
import { EventSettlementsTab } from '../settlements/EventSettlementsTab';
import { EventPaymentsPanel } from '../payments/EventPaymentsPanel';
import { RECEIVABLES_RESOURCE } from '../payments/types';
import { OVERTIME_RESOURCE, SETTLEMENTS_RESOURCE } from '../settlements/types';
import { QUOTES_KEY, QUOTES_RESOURCE } from '../quotes/types';
import { ScheduleTab } from '../schedule/ScheduleTab';
import { RentalsTab } from '../subrentals/RentalsTab';
import { RENTALS_RESOURCE } from '../subrentals/types';
import { STAGES_RESOURCE, TASKS_RESOURCE } from '../schedule/types';
import { CARDS_RESOURCE } from '../planning/types';
import { useCustomScopes } from './EventFields';
import { scopeNames } from './fields';
import { ConflictNotice } from '../ui/ConflictNotice';
import { usePlaceTitle } from '../nav/NavSteps';
import { useUrlState } from '../nav/urlState';
import { RowMenu } from '../ui/RowMenu';
import { EventForm } from './EventForm';
import { EventFiles } from './EventFiles';
import { useCompanySettings } from '../settings/company';
import { addressWithoutName, distanceLabel, distanceText, tollsText, travelText, type ChosenRoute } from './place';
import { PlaceActions } from './PlaceField';
import { RouteDialog } from './RouteDialog';
import { useGeoConfig } from '../geo/config';
import { SettingsDialog } from '../settings/SettingsDialog';
import { VenueNotesForm, VENUES_RESOURCE } from '../settings/VenuesSettings';
import { systemLabel, useSystems } from '../settings/systems';
import { refreshEvents } from './refresh';
import { titleDates, todayInWarsaw } from './calendar';
import { mayRepeat, RepeatDialog, SeriesRow } from './RepeatForm';
import {
  EVENT_STATUSES,
  EVENTS_KEY,
  EVENTS_RESOURCE,
  formatDay,
  formatEventRange,
  formatSegments,
  isClosed,
  mayFinishByHand,
  RESERVATION_LABELS,
  SETTLEMENT_LABELS,
  settlementMarker,
  statusLine,
  type EventStatus,
  type EventView,
} from './labels';
import { DialogLayer } from '../ui/Dialog';
import { ViewersRow } from './ViewersField';
import { leaveGuarded } from '../ui/unsaved';
import { markStyle, useContractorColors } from '../contractors/colors';
import { Notice } from '../ui/Notice';

/** Polish text of a status-change refusal; `planning_incomplete` lists what is missing. */
function statusMessage(error: ApiError): string {
  const missing = (error.details as { missing?: string[] } | undefined)?.missing;
  if (error.error === 'business_rule' && Array.isArray(missing) && missing.length > 0) {
    return `Żeby ustawić ten status, uzupełnij: ${missing.join(', ')}. Zrób to w edycji wydarzenia.`;
  }
  return error.message;
}

/**
 * "Widzą: …" (spec 2026-10-23-widocznosc-wydarzen): the names of the list; with the events edit right a ⚙ changes it
 * (one PATCH of `viewerIds` when the list closes).
 */
function ViewersPanelRow({ event: e, canEdit }: { event: EventView; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (viewerIds: string[]) => api<EventView>(`/api/events/${e.id}`, { method: 'PATCH', body: { version: e.version, viewerIds } }),
    // a 409 (someone changed the event meanwhile): the fresh version comes, the person picks again
    onSettled: () => refreshEvents(queryClient),
  });
  return (
    <>
      <ViewersRow ids={e.viewerIds ?? []} names={e.viewerNames ?? []} onChange={canEdit ? (ids) => save.mutate(ids) : undefined} busy={save.isPending} />
      {save.error && (
        <div>
          <dd className="alert" role="alert">
            {save.error instanceof ApiError && save.error.status === 409 ? 'Ktoś właśnie zmienił to wydarzenie — wybierz jeszcze raz.' : save.error instanceof ApiError ? save.error.message : 'Nie udało się zapisać'}
          </dd>
        </div>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="muted">{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

/**
 * "Miejsce": name and address, "ok. N km od magazynu" with the travel time and tolls of the route (spec
 * 2026-10-22-mapy-google), the "nieprecyzyjna" note, the notes of the place for the crew (⚙ to change them), and the
 * buttons: "Pokaż trasę" (choosing a route needs the edit right), "Nawiguj" (with the address visible), Google Maps,
 * copy (owner remark 3/12).
 */
function PlaceRow({ event: e, canEdit }: { event: EventView; canEdit: boolean }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const { config } = useGeoConfig();
  const settings = useCompanySettings().data;
  const [routes, setRoutes] = useState(false);
  const [notes, setNotes] = useState(false);
  const place = { location: e.location ?? '', locationAddress: e.locationAddress ?? '', locationPoint: e.locationPoint ?? null, locationApprox: e.locationApprox ?? false, googlePlaceId: e.googlePlaceId ?? '', placePoint: e.placePoint ?? null };
  const located = place.locationAddress !== '' || place.locationPoint !== null;
  // which distance is used (owner: "a jak liczysz odległość?"); a role without the source sees the plain "ok. N km"
  const distance = e.distanceSource
    ? distanceText({ routeKm: e.routeKm ?? null, distanceKm: e.distanceKm ?? null, distanceSource: e.distanceSource, chosenLabel: e.chosenRoute?.label })
    : distanceLabel(e.distanceKm);
  const extra = [travelText(e.travelMinutes) && `dojazd ${travelText(e.travelMinutes)}`, tollsText(e.tollsGrosze)].filter(Boolean).join(' · ');
  const mayChoose = canEdit && 'chosenRoute' in e;
  const choose = useMutation({
    mutationFn: ({ route, avoid }: { route: ChosenRoute; avoid: boolean }) =>
      api<EventView>(`/api/events/${e.id}`, {
        method: 'PATCH',
        body: {
          version: e.version,
          // the tolls only for whoever sees them (rule 7a)
          // the route by its id: the server takes its time and tolls (review I2)
          chosenRoute: { ...(route.routeId ? { routeId: route.routeId } : {}), km: route.km, ...(route.minutes !== undefined ? { minutes: route.minutes } : {}), label: route.label, ...('tollsGrosze' in e && route.tollsGrosze !== undefined ? { tollsGrosze: route.tollsGrosze } : {}) },
          ...('avoidTolls' in e && avoid !== e.avoidTolls ? { avoidTolls: avoid } : {}),
        },
      }),
    onSuccess: () => refreshEvents(queryClient),
  });
  const canNotes = !!me && !!e.venueId && can(me, VENUES_RESOURCE, 'edit');
  return (
    <div className="place-row">
      <dt className="muted">Miejsce</dt>
      <dd>
        {place.location || place.locationAddress ? (
          <>
            {place.location && <span className="place-name">{place.location}</span>}
            {place.locationAddress && <span className="place-address">{addressWithoutName(place.locationAddress, place.location)}</span>}
            {(distance || (located && e.locationApprox)) && (
              <span className="place-notes">
                {(distance || extra) && (
                  <span data-feature-id="events.route">
                    {[distance, extra].filter(Boolean).join(' · ')}
                  </span>
                )}
                {located && e.locationApprox && <span className="approx-note">Lokalizacja nieprecyzyjna</span>}
              </span>
            )}
            {(e.placeNotes || canNotes) && (
              <span className="place-crew-notes" data-feature-id="events.venues">
                {e.placeNotes ? <span>Notatki o miejscu: {e.placeNotes}</span> : <span className="muted">Brak notatek o miejscu</span>}
                {canNotes && <RowMenu icon="gear" label="Notatki o miejscu" featureId="events.venues" actions={[{ label: 'Notatki o miejscu…', onSelect: () => setNotes(true) }]} />}
              </span>
            )}
            {choose.error && (
              <span className="field-error" role="alert">
                {choose.error instanceof ApiError ? choose.error.message : 'Nie udało się zapisać trasy'}
              </span>
            )}
            <PlaceActions
              place={place}
              name={e.name ?? ''}
              navigate={'locationAddress' in e}
              before={
                config.routes && place.locationPoint && settings?.warehousePoint ? (
                  <button className="btn small" type="button" data-feature-id="events.route" onClick={() => setRoutes(true)}>
                    Pokaż trasę
                  </button>
                ) : undefined
              }
            />
            {routes && place.locationPoint && (
              <RouteDialog
                to={place.locationPoint}
                startsAt={e.startsAt ?? null}
                avoidTolls={e.avoidTolls ?? false}
                chosen={e.chosenRoute ?? null}
                canChoose={mayChoose}
                onUse={(route, avoid) => choose.mutate({ route, avoid })}
                onClose={() => setRoutes(false)}
              />
            )}
            {notes && e.venueId && (
              <SettingsDialog title="Notatki o miejscu" onClose={() => setNotes(false)}>
                <VenueNotesForm
                  venueId={e.venueId}
                  onDone={() => {
                    setNotes(false);
                    void refreshEvents(queryClient);
                  }}
                />
              </SettingsDialog>
            )}
          </>
        ) : (
          '—'
        )}
      </dd>
    </div>
  );
}

/**
 * "Status" of the details (owner 2026-10-20 23:42): the stage in words ("Do wyceny", "Czeka na akceptację klienta", …)
 * and, from the first day of a confirmed or running event, a chevron with "Oznacz jako zakończone" (edit right only).
 */
function StatusRow({ event: e, canChange, onFinish }: { event: EventView; canChange: boolean; onFinish: () => void }) {
  const status = e.status!;
  // a quote sent and not decided: asked only for an event in "Wycena" (the answer is a stage, no amounts)
  const awaiting = useQuery({
    queryKey: [...QUOTES_KEY, 'client-state', e.id],
    enabled: status === 'wycena',
    retry: false,
    queryFn: () => api<{ awaitingClient?: boolean }>(`/api/quotes/events/${e.id}/client-state`),
  });
  const line = statusLine(status, status === 'wycena' && awaiting.data?.awaitingClient === true);
  return (
    <div>
      <dt className="muted">Status</dt>
      <dd className="status-line" data-feature-id="events.finished">
        <span className="status" data-status={status}>
          {line}
        </span>
        {canChange && mayFinishByHand(e, todayInWarsaw()) && <RowMenu icon="chevron" label={`Zmień status: ${line}`} actions={[{ label: 'Oznacz jako zakończone', onSelect: onFinish }]} />}
      </dd>
    </div>
  );
}

/**
 * Whether the card's content has gone under its pinned head (owner 2026-10-22 01:04): the sentinel just above the head
 * has left the visible part of its scroll box (the panel, or the page in "Pełny widok"). No IntersectionObserver
 * (old browsers, tests): never "stuck", the head just has no hairline.
 */
function useStuck(sentinel: { current: Element | null }): boolean {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || typeof IntersectionObserver !== 'function') return;
    const io = new IntersectionObserver(([entry]) => setStuck(!!entry && !entry.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [sentinel]);
  return stuck;
}

/** Event card with tabs. "Harmonogram" will appear here with a later feature. */
export type EventTab = 'details' | 'packlist' | 'inputlist' | 'crew' | 'schedule' | 'checklist' | 'quote' | 'subrentals' | 'profit' | 'settlements' | 'payments';
const TAB_TITLES: Record<EventTab, string> = { details: 'Szczegóły', packlist: 'Sprzętówka', inputlist: 'Inputlista', crew: 'Obsada', schedule: 'Harmonogram', checklist: 'Lista kontrolna', quote: 'Kosztorys', subrentals: 'Dopożyczenia', profit: 'Rentowność', settlements: 'Rozliczenia', payments: 'Płatności' };

export function EventDetails({
  eventId,
  onClose,
  initialTab = 'details',
  notice,
  onTabChange,
  full = false,
  onFullChange,
  phone = false,
}: {
  eventId: string;
  onClose: () => void;
  initialTab?: EventTab;
  notice?: string | undefined;
  /** The page keeps the open tab in the address. */
  onTabChange?: ((tab: EventTab) => void) | undefined;
  /** Owner remark 14 (P6): the open tab over the whole window ("Pełny widok") or beside the calendar ("Zmniejsz"). */
  full?: boolean;
  onFullChange?: ((full: boolean) => void) | undefined;
  /** On a phone the card covers the screen anyway: no full-view button. */
  phone?: boolean;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [tab, setTabState] = useState<EventTab>(initialTab);
  const sentinel = useRef<HTMLSpanElement>(null);
  const stuck = useStuck(sentinel);
  // the tabs scroll sideways in a narrow panel: the chosen one is always in view (owner 20.10, "Kosztorys" was cut off)
  const tabList = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const reveal = () => {
      const list = tabList.current;
      const chosen = list?.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!list || !chosen) return;
      const l = list.getBoundingClientRect();
      const c = chosen.getBoundingClientRect();
      if (c.right > l.right) list.scrollLeft += c.right - l.right + 8;
      else if (c.left < l.left) list.scrollLeft -= l.left - c.left + 8;
    };
    reveal();
    // the web font widens the tabs, and the panel's scroll bar narrows the strip once the tab's content is in
    let live = true;
    void document.fonts?.ready.then(() => live && reveal());
    const resized = typeof ResizeObserver === 'function' && tabList.current ? new ResizeObserver(reveal) : null;
    if (resized && tabList.current) resized.observe(tabList.current);
    return () => {
      live = false;
      resized?.disconnect();
    };
  }, [tab]);
  /** "Wycena" on the details opens the quote and creates it when there is none (owner remark 17). */
  const [createQuote, setCreateQuote] = useState(false);
  /** "Przejdź do kosztorysu" of the packlist (owner 2026-10-21 23:31): what was done, said on the quote. */
  const [quoteNotice, setQuoteNotice] = useState<GoToQuoteNotice | null>(null);
  /** Owner 2026-10-30: what saving the scope did to the quote (the edit form closes, the card says it). */
  const [scopeNotice, setScopeNotice] = useState<string | null>(null);
  const card = useRef<HTMLElement>(null);
  // another tab with unsaved changes in this one (a form, a sheet row) asks first (owner 2026-10-23 21:14)
  const setTab = (t: EventTab) =>
    leaveGuarded(() => {
      setTabState(t);
      // only the "Wycena" button asks for a quote to be created; any other move between tabs forgets it
      setCreateQuote(false);
      setQuoteNotice(null);
      onTabChange?.(t);
    }, card.current);
  // a link inside the card to another of its tabs (e.g. „Obsada” in the quote's crew hover, owner 2026-10-25) changes
  // only the address: the card follows it. The app-wide guard (`UnsavedChangesHost`: links and „Wstecz”) has already
  // asked about unsaved changes before the address changed — no second question, and the address never runs ahead
  const linkedTab = useRef(initialTab);
  useEffect(() => {
    if (linkedTab.current === initialTab) return;
    linkedTab.current = initialTab;
    if (initialTab === tab) return;
    setTabState(initialTab);
    setCreateQuote(false);
    setQuoteNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);
  const { custom } = useCustomScopes(false);
  const systems = useSystems();
  // P13: the open edit form is in the address (`edycja=1`), so "Wstecz / Dalej" comes back to it with its draft
  const { params, update } = useUrlState();
  const editing = params.get('edycja') === '1';
  const setEditing = (v: boolean) => update({ edycja: v ? '1' : null });
  const [showHistory, setShowHistory] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** "Powtórz…" (owner 2026-10-21 06:58): the series dialog. */
  const [repeating, setRepeating] = useState(false);
  const [status, setStatus] = useState<EventStatus | ''>('');
  /** "Rezygnacja klienta" (P6): its day (today unless changed) and a short note. */
  const [resignationOn, setResignationOn] = useState(() => todayInWarsaw());
  const [resignationNote, setResignationNote] = useState('');
  const event = useQuery({ queryKey: [...EVENTS_KEY, 'one', eventId], queryFn: () => api<EventView>(`/api/events/${eventId}`) });

  const contractorId = event.data?.contractorId ?? null;
  // owner 2026-10-23 20:49 / 2026-10-24 10:35: the head is tinted in the client's colour (only when the client came:
  // rule 7a) and carries the status stripe at its left edge
  const clientColor = useContractorColors().get(contractorId ?? '');
  // The client's name is fetched by id, so it does not depend on what the list screen has filtered.
  const client = useQuery({
    queryKey: ['contractors', 'one', contractorId],
    enabled: contractorId !== null,
    retry: false,
    queryFn: () => api<{ name: string }>(`/api/contractors/${contractorId}`),
  });
  const clientName = client.data?.name ?? '';

  const change = useMutation({
    mutationFn: (to: EventStatus) =>
      api<EventView>(`/api/events/${eventId}/status`, {
        method: 'POST',
        body: {
          status: to,
          version: event.data!.version,
          // only the fields the role may write (rule 7a); the server takes today for a day not sent
          ...(to === 'rezygnacja' && me && fieldVisible(me, EVENTS_RESOURCE, 'resignationOn') && resignationOn ? { resignationOn } : {}),
          ...(to === 'rezygnacja' && me && fieldVisible(me, EVENTS_RESOURCE, 'resignationNote') && resignationNote.trim() ? { resignationNote: resignationNote.trim() } : {}),
        },
      }),
    onSuccess: async () => {
      setStatus('');
      setResignationNote('');
      await refreshEvents(queryClient);
    },
  });
  /** After "Uzupełnij planowanie" from an accepted quote: 'armed' while the form is open, then the saved event to confirm. */
  const [offerConfirm, setOfferConfirm] = useState<null | 'armed' | EventView>(null);
  const confirm = useMutation({
    mutationFn: (saved: EventView) => api<EventView>(`/api/events/${eventId}/status`, { method: 'POST', body: { status: 'potwierdzone', version: saved.version } }),
    onSuccess: async () => {
      setOfferConfirm(null);
      await queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/events/${eventId}?version=${event.data!.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await refreshEvents(queryClient);
      onClose();
    },
  });

  // P13: the name of this place in the list of recent places, e.g. "Kosztorys — Wydarzenie X"
  const named = event.data?.name;
  usePlaceTitle(named ? (tab === 'details' ? named : `${TAB_TITLES[tab]} — ${named}`) : null);
  if (!me) return null;
  const e = event.data;
  const error = change.error instanceof ApiError ? change.error : null;
  const conflict = error?.status === 409;
  const removeError = remove.error instanceof ApiError ? remove.error : null;
  const reload = () => {
    change.reset();
    remove.reset();
    setDeleting(false);
    void refreshEvents(queryClient);
  };

  return (
    <section ref={card} className="panel event-card" aria-label="Karta wydarzenia">
      {/* owner 2026-10-22 01:04: the head and the tabs stay pinned, the content scrolls under them (a hairline once it does) */}
      <span ref={sentinel} className="event-card-sentinel" aria-hidden="true" />
      <div className={`event-card-top${stuck ? ' stuck' : ''}`}>
      <div className={`event-card-head${clientColor ? ' client-mark' : ''}`} data-status={event.data?.status} style={clientColor ? markStyle(clientColor) : undefined}>
        <div className="event-card-title">
          {/* owner 2026-10-22 01:04: "tytuł musi zawierać datę" — "Festiwal XYZ · 14–15.11.2026" */}
          <h2 tabIndex={-1}>
            {e?.name || 'Wydarzenie'}
            {e && titleDates(e) && (
              <>
                {' '}
                <span className="event-card-dates">· {titleDates(e)}</span>
              </>
            )}
          </h2>
          {e && (
            <div className="event-card-flags">
              {e.status && (
                <span className="status" data-status={e.status}>
                  {EVENT_STATUSES[e.status]}
                </span>
              )}
              {e.needsQuote && <span className="quote-flag">Potrzebna wycena</span>}
              {e.kind === 'wypozyczenie' && <span className="chip">Wypożyczenie</span>}
              {settlementMarker(e.settlement) && <span className="chip">{settlementMarker(e.settlement)}</span>}
            </div>
          )}
        </div>
        <div className="event-card-tools">
          {e && (
            <RowMenu
              label={`Więcej działań: ${e.name || 'Wydarzenie'}`}
              actions={[
                ...(can(me, 'history.entries', 'view')
                  ? [{ label: showHistory ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => leaveGuarded(() => { setTab('details'); setEditing(false); setShowHistory(!showHistory); }, card.current) }]
                  : []),
                ...(mayRepeat(me, e) ? [{ label: 'Powtórz…', onSelect: () => setRepeating(true) }] : []),
                ...(can(me, EVENTS_RESOURCE, 'delete') ? [{ label: 'Usuń wydarzenie', danger: true, onSelect: () => leaveGuarded(() => { setTab('details'); setDeleting(true); }, card.current) }] : []),
              ]}
            />
          )}
          {onFullChange && !phone && (
            <button className="btn ghost small" type="button" data-feature-id="events.list" onClick={() => onFullChange(!full)}>
              {full ? 'Zmniejsz' : 'Pełny widok'}
            </button>
          )}
          <button className="btn ghost small" type="button" onClick={onClose}>
            Zamknij
          </button>
        </div>
      </div>
      <div role="tablist" aria-label="Zakładki wydarzenia" className="segment event-card-tabs" ref={tabList}>
        <button role="tab" type="button" aria-selected={tab === 'details'} onClick={() => setTab('details')}>
          Szczegóły
        </button>
        {can(me, PACKLISTS_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'packlist'} onClick={() => setTab('packlist')}>
            Sprzętówka
          </button>
        )}
        {can(me, INPUTLISTS_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'inputlist'} onClick={() => setTab('inputlist')}>
            Inputlista
          </button>
        )}
        {can(me, CREW_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'crew'} onClick={() => setTab('crew')}>
            Obsada
          </button>
        )}
        {(can(me, STAGES_RESOURCE, 'view') || can(me, TASKS_RESOURCE, 'view')) && (
          <button role="tab" type="button" aria-selected={tab === 'schedule'} onClick={() => setTab('schedule')}>
            Harmonogram
          </button>
        )}
        {can(me, CARDS_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'checklist'} onClick={() => setTab('checklist')}>
            Lista kontrolna
          </button>
        )}
        {can(me, QUOTES_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'quote'} onClick={() => setTab('quote')}>
            Kosztorys
          </button>
        )}
        {can(me, RENTALS_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'subrentals'} onClick={() => setTab('subrentals')}>
            Dopożyczenia
          </button>
        )}
        {/* F29: by default the owner only */}
        {can(me, PROFIT_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'profit'} onClick={() => setTab('profit')} data-feature-id="finance.profitability">
            Rentowność
          </button>
        )}
        {/* F39: the people's settlements; the event's manager sees the overtime only */}
        {(can(me, SETTLEMENTS_RESOURCE, 'view') || can(me, OVERTIME_RESOURCE, 'view') || can(me, EVENT_COSTS_RESOURCE, 'view')) && (
          <button role="tab" type="button" aria-selected={tab === 'settlements'} onClick={() => setTab('settlements')} data-feature-id="settlements.card">
            Rozliczenia
          </button>
        )}
        {/* F36: the payment plan, receivables and payments of the event */}
        {can(me, RECEIVABLES_RESOURCE, 'view') && (
          <button role="tab" type="button" aria-selected={tab === 'payments'} onClick={() => setTab('payments')} data-feature-id="payments.plan">
            Płatności
          </button>
        )}
      </div>
      </div>
      {notice && (
        <Notice resetKey={notice}>
          {notice}
        </Notice>
      )}
      {scopeNotice && (
        <Notice resetKey={scopeNotice} data-feature-id="quotes.scope_lines" onClose={() => setScopeNotice(null)}>
          {scopeNotice}
        </Notice>
      )}
      {event.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {event.error && <p className="alert" role="alert">{event.error instanceof ApiError ? event.error.message : 'Nie udało się wczytać wydarzenia'}</p>}
      {e && tab === 'packlist' && (
        <PacklistTab
          eventId={eventId}
          onGoToQuote={(text) => {
            setTab('quote');
            setQuoteNotice(text);
          }}
        />
      )}
      {e && tab === 'inputlist' && <InputlistTab eventId={eventId} />}
      {e && tab === 'crew' && <CrewTab event={e} />}
      {e && tab === 'schedule' && <ScheduleTab event={e} />}
      {e && tab === 'checklist' && <ChecklistPanel eventId={eventId} />}
      {e && tab === 'subrentals' && <RentalsTab eventId={eventId} />}
      {e && tab === 'profit' && <ProfitTab eventId={eventId} />}
      {e && tab === 'settlements' && <EventSettlementsTab eventId={eventId} me={me} />}
      {e && tab === 'payments' && <EventPaymentsPanel eventId={eventId} me={me} settlement={e.settlement} />}
      {e && tab === 'quote' && (
        <QuoteTab
          eventId={eventId}
          clientName={clientName}
          autoCreate={createQuote}
          notice={quoteNotice}
          onNoticeClose={() => setQuoteNotice(null)}
          onAutoCreateDone={() => setCreateQuote(false)}
          eventStatus={e.status}
          settlement={e.settlement}
          onCompletePlanning={() => {
            setTab('details');
            setEditing(true);
            setOfferConfirm('armed');
          }}
        />
      )}
      {e && tab === 'details' && !editing && (
        <div role="tabpanel" aria-label="Szczegóły">
          <dl className="details-list">
            {e.status !== undefined && <StatusRow event={e} canChange={can(me, EVENTS_RESOURCE, 'edit')} onFinish={() => change.mutate('zakonczone')} />}
            {/* several terms (owner 2026-10-21 00:44): each of them; gear and people are free in between */}
            {e.segments && e.segments.length > 1 ? (
              <Row label={`Terminy (${e.segments.length})`} value={formatSegments(e.segments)} />
            ) : (
              <Row label="Termin" value={formatEventRange(e.startsAt, e.endsAt)} />
            )}
            <SeriesRow eventId={e.id} />
            {e.contractorId !== undefined && <Row label="Klient" value={clientName} />}
            {e.managerUserId !== undefined && <Row label="Odpowiedzialny" value={e.managerName ?? ''} />}
            {e.viewerIds !== undefined && <ViewersPanelRow event={e} canEdit={can(me, EVENTS_RESOURCE, 'edit')} />}
            {e.location !== undefined && <PlaceRow event={e} canEdit={can(me, EVENTS_RESOURCE, 'edit')} />}
            {e.settlement !== undefined && <Row label="Rozliczenie" value={SETTLEMENT_LABELS[e.settlement]} />}
            {e.reservationKind !== undefined && <Row label="Rezerwacja sprzętu" value={RESERVATION_LABELS[e.reservationKind]} />}
            {e.systemId !== undefined && <Row label="System" value={systemLabel(systems.data?.systems ?? [], e.systemId)} />}
            {e.equipmentSource !== undefined && <Row label="Skąd sprzęt" value={e.equipmentSource} />}
            {e.scopes !== undefined && <Row label="Zakres prac" value={scopeNames(e.scopes, custom).join(', ')} />}
            {e.notes !== undefined && <Row label="Uwagi" value={e.notes} />}
            {e.status === 'rezygnacja' && (e.resignationOn !== undefined || e.resignationNote !== undefined) && (
              <Row label="Rezygnacja klienta" value={[e.resignationOn ? formatDay(e.resignationOn) : '', e.resignationNote ?? ''].filter(Boolean).join(' — ')} />
            )}
          </dl>
          <RemindersSection eventId={e.id} />
          <EventFiles eventId={e.id} />

          {can(me, EVENTS_RESOURCE, 'edit') && (
            <div className="field-row" data-feature-id="events.status">
              <label className="field">
                <span>Nowy status</span>
                <select value={status} onChange={(ev) => setStatus(ev.target.value as EventStatus | '')}>
                  <option value="">Wybierz status</option>
                  {(Object.keys(EVENT_STATUSES) as EventStatus[]).filter((s) => s !== e.status && (!isClosed(e.status) || s === 'zapytanie')).map((s) => (
                    <option key={s} value={s}>
                      {EVENT_STATUSES[s]}
                    </option>
                  ))}
                </select>
              </label>
              <button className="btn" type="button" disabled={status === '' || change.isPending} onClick={() => status && change.mutate(status)}>
                Zmień status
              </button>
            </div>
          )}
          {status === 'rezygnacja' && (
            <div className="field-row">
              {fieldVisible(me, EVENTS_RESOURCE, 'resignationOn') && (
                <label className="field">
                  <span>Data rezygnacji</span>
                  <input type="date" value={resignationOn} onChange={(ev) => setResignationOn(ev.target.value)} />
                </label>
              )}
              {fieldVisible(me, EVENTS_RESOURCE, 'resignationNote') && (
                <label className="field">
                  <span>Notatka do rezygnacji</span>
                  <input type="text" maxLength={2000} value={resignationNote} placeholder="np. wybrali inną firmę" onChange={(ev) => setResignationNote(ev.target.value)} />
                </label>
              )}
            </div>
          )}
          {conflict && <ConflictNotice onReload={reload} />}
          {error && !conflict && (
            <p className="alert" role="alert">
              {statusMessage(error)}
            </p>
          )}

          {(can(me, QUOTES_RESOURCE, 'view') || can(me, EVENTS_RESOURCE, 'edit')) && (
            // owner remark 18: small buttons, one under another; history and delete wait in "…" by the title
            <div className="actions stack" role="group" aria-label="Działania wydarzenia">
              {can(me, QUOTES_RESOURCE, 'view') && (
                <button
                  className="btn small primary"
                  type="button"
                  data-feature-id="quotes.tab"
                  onClick={() => {
                    setTab('quote');
                    setCreateQuote(true);
                  }}
                >
                  Wycena
                </button>
              )}
              {can(me, EVENTS_RESOURCE, 'edit') && (
                <button className="btn small" type="button" onClick={() => setEditing(true)}>
                  Edytuj wydarzenie
                </button>
              )}
            </div>
          )}
          {showHistory && <HistoryPanel entityId={e.id} resource={EVENTS_RESOURCE} />}
        </div>
      )}
      {e && repeating && <RepeatDialog event={e} onClose={() => setRepeating(false)} />}
      {e && tab === 'details' && deleting && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="delete-event-title">
            <DialogLayer onClose={() => { setDeleting(false); remove.reset(); }} busy={remove.isPending} />
            <h2 id="delete-event-title">Usunąć wydarzenie?</h2>
            <p>„{e.name || 'Wydarzenie'}” zniknie z listy. Wydarzenie zostaje w bazie, a w historii zmian zostaje wpis o usunięciu.</p>
            {removeError?.status === 409 && <ConflictNotice onReload={reload} />}
            {removeError && removeError.status !== 409 && (
              <p className="alert" role="alert">
                {removeError.message}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
                {remove.isPending ? 'Usuwanie…' : 'Usuń wydarzenie'}
              </button>
              <button className="btn" type="button" onClick={() => { setDeleting(false); remove.reset(); }}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {e && tab === 'details' && editing && (
        <EventForm
          key={e.version}
          event={e}
          clientName={clientName}
          // opened from an accepted quote: once the planning is saved, offer to confirm the event
          onSaved={(saved) => offerConfirm === 'armed' && (saved.status === 'zapytanie' || saved.status === 'wycena') && setOfferConfirm(saved)}
          onQuoteNotice={setScopeNotice}
          onDone={() => {
            setEditing(false);
            setOfferConfirm((o) => (o === 'armed' ? null : o));
          }}
        />
      )}
      {offerConfirm && offerConfirm !== 'armed' && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="confirm-event-title">
            <DialogLayer onClose={() => setOfferConfirm(null)} busy={confirm.isPending} />
            <h2 id="confirm-event-title">Potwierdzić wydarzenie?</h2>
            <p>Klient zaakceptował kosztorys, a planowanie jest zapisane. Zmienić status na „Potwierdzone”?</p>
            {confirm.error && (
              <p className="alert" role="alert">
                {confirm.error instanceof ApiError ? confirm.error.message : 'Nie udało się zmienić statusu'}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" autoFocus disabled={confirm.isPending} onClick={() => confirm.mutate(offerConfirm)}>
                Potwierdź wydarzenie
              </button>
              <button className="btn" type="button" onClick={() => setOfferConfirm(null)}>
                Nie teraz
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
